/**
 * Session synchronization hook
 * Handles subscription state persistence across page refreshes
 * Extracted from original lines 153-176
 */

import { useState, useEffect, useCallback } from "react";
import {
  loadSubscriptionFromSession,
  saveSubscriptionToSession,
  clearSubscriptionSession,
} from "../utils/sessionStorage";
import { SubscriptionSessionState, CategoryType } from "../StructureNote.types";

/**
 * Hook to sync subscription state with sessionStorage
 * Handles page refresh recovery
 * Original logic from lines 153-176
 *
 * @returns Session state and management functions
 */
export const useSessionSync = () => {
  const [sessionState, setSessionState] = useState<SubscriptionSessionState | null>(null);
  const [category, setCategory] = useState<CategoryType>("view");

  /**
   * Load subscription state from session storage on mount
   * Original logic from lines 153-176
   */
  useEffect(() => {
    const loadedState = loadSubscriptionFromSession();
    if (loadedState) {
      setSessionState(loadedState);
      setCategory("subscribe");
    }
  }, []);

  /**
   * Save subscription state to session storage
   */
  const saveSession = useCallback((state: Partial<SubscriptionSessionState>) => {
    saveSubscriptionToSession(state);
    if (state.category) {
      setCategory(state.category);
    }
  }, []);

  /**
   * Clear subscription session
   */
  const clearSession = useCallback(() => {
    clearSubscriptionSession();
    setSessionState(null);
    setCategory("view");
  }, []);

  return {
    sessionState,
    category,
    setCategory,
    saveSession,
    clearSession,
    hasSession: sessionState !== null,
  };
};
